import React, {useEffect, useState} from 'react'
import SpinnerLoading from '@components/V3/loadingIndicators/Spinner'
import classnames from '@helpers/misc/classnames'
import useStatus, {TrackingStatus} from '@page-components/TrackV2/hooks/useStatus'
import {LottiePlayer} from 'lottie-web'

let lottie: LottiePlayer = null

type Animation = 'confirmation' | 'delivery' | 'done' | 'preparation' | 'waitingForDelivery'
const availableAnimations: Array<Animation> = [
  'confirmation',
  'delivery',
  'done',
  'preparation',
  'waitingForDelivery'
]

const shouldLoop = (anim: Animation) => {
  return anim !== 'done'
}

const loadLottieAnimation = async (anim: Animation) => {
  if (!availableAnimations.includes(anim)) {
    return null
  }

  const animationData = await (await import(`./animations/${anim}.json`)).default
  if (!lottie) {
    lottie = await (await import('lottie-web')).default
  }

  lottie.destroy()

  lottie.loadAnimation({
    container: document.querySelector('#tracking-animation'),
    animationData: animationData,
    loop: shouldLoop(anim),
    rendererSettings: {
      preserveAspectRatio: 'xMidYMid slice'
    }
  })

  lottie.play()
}

const mapStatusToAnimation = (status: TrackingStatus): Animation => {
  if (status === 'archived') {
    return 'done'
  }

  if (status === 'readyForPickup') {
    return 'waitingForDelivery'
  }

  if (status === 'inReview') {
    return 'confirmation'
  }

  if (availableAnimations.includes(status as Animation)) {
    return status as Animation
  }

  return 'preparation'
}

export default function TrackingAnimation() {
  const status = useStatus()
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    setLoading(true)
    loadLottieAnimation(mapStatusToAnimation(status)).then(() => {
      setLoading(false)
    })
  }, [status])

  return (
    <div className="bg-gray-100 dark:bg-zinc-100 lg:min-h-[400px] lg:h-full w-full flex items-center justify-center">
      <div id="tracking-animation" className={classnames('h-full', {hidden: loading})}></div>
      <SpinnerLoading className={classnames({hidden: !loading})} />
    </div>
  )
}
